import { createInstance, Identify } from '@amplitude/analytics-browser';

const amplitude = createInstance();
const API_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY
  || '734976c9cffcb1e276760aeb7afe557d';
const APP_OPEN_KEY = 'bigsport_app_open_tracked';
const FIRST_TOUCH_KEY = 'bigsport_analytics_first_touch';
const LANDING_PAGE_KEY = 'bigsport_analytics_landing_page';
const HUMAN_CONFIRMATION_DELAY_MS = 5000;
const ENGAGED_SESSION_DELAY_MS = 10000;
const HUMAN_EVENTS = [
  'mousemove',
  'touchstart',
  'scroll',
  'keydown',
  'pointerdown',
];
const BOT_USER_AGENT_PATTERN = new RegExp([
  'headlesschrome',
  'googlebot',
  'google-inspectiontool',
  'adsbot',
  'bingbot',
  'yandexbot',
  'semrushbot',
  'ahrefsbot',
  'facebookexternalhit',
  'facebot',
  'slackbot',
  'telegrambot',
  'discordbot',
  'applebot',
  'crawler',
  'spider',
  '\\bbot\\b',
  'lighthouse',
  'pagespeed',
  'chrome-lighthouse',
  'prerender',
  'rendertron',
  'phantomjs',
  'puppeteer',
  'selenium',
].join('|'), 'i');
const PRIVATE_PROPERTY_KEYS = new Set([
  'email',
  'display_name',
  'displayName',
  'username',
  'password',
  'token',
  'comment',
  'comment_text',
  'bio',
  'birth_date',
  'birthDate',
  'ip',
]);

let isInitialized = false;
let lastPageKey = null;
let pageViewCount = 0;
let humanConfirmed = false;
let humanTrackingStarted = false;
let appOpenRequested = false;
let engagedSession = false;
let humanTimer = null;
let engagedTimer = null;
let removeHumanListeners = null;
const recentActions = new Map();
let analyticsContext = {
  is_authenticated: false,
  role: 'guest',
  is_internal: false,
  engaged_session: false,
};

const getHostname = () => (typeof window === 'undefined' ? '' : window.location.hostname);
const isDevelopment = () => process.env.NODE_ENV === 'development';
const debugAnalytics = (message, details = {}) => {
  if (isDevelopment()) {
    console.debug(`[AMPLITUDE] ${message}`, details);
  }
};

export const getBotDetection = () => {
  if (typeof navigator === 'undefined') {
    return { isBot: false, reason: null };
  }
  if (navigator.webdriver === true) {
    return { isBot: true, reason: 'navigator.webdriver' };
  }
  const userAgent = String(navigator.userAgent || '');
  const match = userAgent.match(BOT_USER_AGENT_PATTERN);
  return {
    isBot: Boolean(match),
    reason: match ? `user_agent:${match[0].toLowerCase()}` : null,
  };
};

export const isSuspectedBot = () => getBotDetection().isBot;

export const isAnalyticsEnabled = () => {
  if (process.env.REACT_APP_AMPLITUDE_DEBUG === 'true') return true;
  return process.env.NODE_ENV === 'production'
    && !['localhost', '127.0.0.1', ''].includes(getHostname());
};

const getDeviceType = () => {
  if (typeof window === 'undefined') return 'unknown';
  if (window.matchMedia?.('(max-width: 767px)').matches) return 'mobile';
  if (window.matchMedia?.('(max-width: 1024px)').matches) return 'tablet';
  return 'desktop';
};

const getBrowser = () => {
  if (typeof navigator === 'undefined') return 'unknown';
  const userAgent = navigator.userAgent || '';
  if (/Edg\//i.test(userAgent)) return 'edge';
  if (/OPR\//i.test(userAgent)) return 'opera';
  if (/Firefox\//i.test(userAgent)) return 'firefox';
  if (/CriOS\//i.test(userAgent)) return 'chrome_ios';
  if (/Chrome\//i.test(userAgent)) return 'chrome';
  if (/Safari\//i.test(userAgent)) return 'safari';
  return 'other';
};

const getOperatingSystem = () => {
  if (typeof navigator === 'undefined') return 'unknown';
  const value = `${navigator.userAgent || ''} ${navigator.platform || ''}`;
  if (/Android/i.test(value)) return 'android';
  if (/iPhone|iPad|iPod/i.test(value)) return 'ios';
  if (/Windows/i.test(value)) return 'windows';
  if (/Mac/i.test(value)) return 'macos';
  if (/Linux/i.test(value)) return 'linux';
  return 'other';
};

const getLandingPage = () => {
  if (typeof window === 'undefined') return '/';
  const existing = sessionStorage.getItem(LANDING_PAGE_KEY);
  if (existing) return existing;
  const landingPage = window.location.pathname || '/';
  sessionStorage.setItem(LANDING_PAGE_KEY, landingPage);
  return landingPage;
};

const getReferrer = () => {
  if (typeof document === 'undefined' || !document.referrer) return 'direct';
  try {
    return new URL(document.referrer).hostname;
  } catch {
    return 'unknown';
  }
};

const getAutomaticProperties = () => ({
  language: typeof navigator === 'undefined' ? 'unknown' : navigator.language,
  timezone: (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
    } catch {
      return 'unknown';
    }
  })(),
  screen_size: typeof window === 'undefined'
    ? 'unknown'
    : `${window.screen?.width || 0}x${window.screen?.height || 0}`,
  device_type: getDeviceType(),
  browser: getBrowser(),
  os: getOperatingSystem(),
  traffic_source: getFirstTouchSource(),
  referrer: getReferrer(),
  landing_page: getLandingPage(),
});

const sanitizeProperties = (properties = {}) => Object.fromEntries(
  Object.entries(properties)
    .filter(([key, value]) => !PRIVATE_PROPERTY_KEYS.has(key) && value !== undefined)
    .map(([key, value]) => [
      key,
      typeof value === 'string' ? value.slice(0, 100) : value,
    ]),
);

export const resolveTrafficSource = () => {
  if (typeof window === 'undefined') return 'direct';
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source')?.toLocaleLowerCase('uk-UA');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');

  if (utmSource || utmMedium || utmCampaign) return 'campaign';

  let hostname = '';
  try {
    hostname = document.referrer ? new URL(document.referrer).hostname.toLowerCase() : '';
  } catch {
    hostname = '';
  }
  if (!hostname) return 'direct';
  if (hostname.includes('google.')) return 'google';
  if (hostname.includes('facebook.com') || hostname.includes('fb.com')) return 'facebook';
  if (hostname.includes('instagram.com')) return 'instagram';
  if (hostname.includes('t.me') || hostname.includes('telegram.')) return 'telegram';
  if (hostname.includes('linkedin.com')) return 'linkedin';
  return 'other_referral';
};

export const getFirstTouchSource = () => {
  if (typeof window === 'undefined') return 'direct';
  const existing = sessionStorage.getItem(FIRST_TOUCH_KEY);
  if (existing) return existing;
  const source = resolveTrafficSource();
  sessionStorage.setItem(FIRST_TOUCH_KEY, source);
  return source;
};

export function initAmplitude() {
  if (isInitialized) return true;
  if (!isAnalyticsEnabled()) {
    debugAnalytics('init skipped', { reason: 'analytics_disabled' });
    return false;
  }
  const botDetection = getBotDetection();
  if (botDetection.isBot) {
    debugAnalytics('init skipped', {
      reason: 'suspected_bot',
      bot_reason: botDetection.reason,
    });
    return false;
  }
  amplitude.init(API_KEY, undefined, { defaultTracking: false });
  isInitialized = true;
  debugAnalytics('initialized');
  return true;
}

export const setAnalyticsContext = ({ isAuthenticated, role } = {}) => {
  const normalizedRole = ['user', 'author', 'admin'].includes(role) ? role : 'guest';
  analyticsContext = {
    is_authenticated: Boolean(isAuthenticated),
    role: normalizedRole,
    is_internal: normalizedRole === 'admin' || normalizedRole === 'author',
    engaged_session: engagedSession,
  };
};

export function trackEvent(eventName, eventProperties = {}) {
  if (!eventName) {
    debugAnalytics('event skipped', { reason: 'missing_event_name' });
    return false;
  }
  if (!isInitialized || !isAnalyticsEnabled()) {
    debugAnalytics('event skipped', {
      event_name: eventName,
      reason: 'analytics_not_initialized',
    });
    return false;
  }
  const botDetection = getBotDetection();
  if (botDetection.isBot) {
    debugAnalytics('event skipped', {
      event_name: eventName,
      reason: 'suspected_bot',
      bot_reason: botDetection.reason,
    });
    return false;
  }
  const pagePath = typeof window === 'undefined' ? '' : window.location.pathname;
  if (pagePath.startsWith('/admin')) {
    debugAnalytics('event skipped', {
      event_name: eventName,
      reason: 'admin_route',
    });
    return false;
  }

  const properties = sanitizeProperties({
    app_environment: process.env.NODE_ENV,
    app_version: process.env.REACT_APP_VERSION || '5.2',
    page_path: pagePath,
    ...getAutomaticProperties(),
    ...analyticsContext,
    ...eventProperties,
  });

  debugAnalytics('event sent', {
    event_name: eventName,
    properties,
    has_user_id: analyticsContext.is_authenticated,
  });
  amplitude.track(eventName, properties);
  return true;
}

export const claimAnalyticsAction = (key, dedupeWindowMs = 1500) => {
  if (!key) return true;
  const now = Date.now();
  const previous = recentActions.get(key);
  if (previous && now - previous < dedupeWindowMs) return false;
  recentActions.set(key, now);

  if (recentActions.size > 200) {
    recentActions.forEach((timestamp, storedKey) => {
      if (now - timestamp >= dedupeWindowMs) recentActions.delete(storedKey);
    });
  }
  return true;
};

const toIsoString = (value) => {
  if (!value) return null;
  try {
    const date = typeof value.toDate === 'function'
      ? value.toDate()
      : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  } catch {
    return null;
  }
};

const resolveFavoriteTeam = (userData) => {
  const favoriteTeam = userData?.sportsPreferences?.favoriteTeam
    ?? userData?.favorite_team
    ?? userData?.favoriteTeam;
  if (favoriteTeam && typeof favoriteTeam === 'object') {
    return favoriteTeam.name || favoriteTeam.id || null;
  }
  return favoriteTeam || null;
};

const resolveRegistrationSource = (userData) => {
  const explicitSource = userData?.registration_source
    || userData?.registrationSource;
  if (explicitSource) return explicitSource;
  const provider = userData?.authProvider
    || userData?.providerId
    || userData?.provider;
  if (provider === 'google.com') return 'google';
  if (provider === 'password') return 'email';
  return provider || 'unknown';
};

export function identifyUser(userId, userData = {}) {
  if (!isInitialized || !isAnalyticsEnabled() || !userId) return false;
  const botDetection = getBotDetection();
  if (botDetection.isBot) {
    debugAnalytics('identify skipped', {
      reason: 'suspected_bot',
      bot_reason: botDetection.reason,
    });
    return false;
  }
  const role = ['user', 'author', 'admin'].includes(userData.role) ? userData.role : 'user';
  setAnalyticsContext({ isAuthenticated: true, role });
  const displayName = userData.displayName || userData.username || null;
  const userProperties = {
    uid: userId,
    display_name: displayName,
    email: userData.email || null,
    role,
    is_authenticated: true,
    is_internal: role === 'admin' || role === 'author',
    favorite_team: resolveFavoriteTeam(userData),
    author: role === 'author' || role === 'admin',
    created_at: toIsoString(userData.createdAt),
    registration_source: resolveRegistrationSource(userData),
    engaged_session: engagedSession,
    ...getAutomaticProperties(),
  };
  const identify = new Identify();
  Object.entries(userProperties).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      identify.set(key, value);
    }
  });
  amplitude.setUserId(userId);
  amplitude.identify(identify);
  debugAnalytics('user identified', {
    user_id: userId,
    user_properties: userProperties,
  });
  return true;
}

export function resetUser() {
  setAnalyticsContext({ isAuthenticated: false, role: 'guest' });
  if (!isInitialized || !isAnalyticsEnabled()) return false;
  if (typeof amplitude.reset === 'function') {
    amplitude.reset();
  } else {
    amplitude.setUserId(undefined);
  }
  debugAnalytics('user identity reset');
  return true;
}

export const trackAppOpen = () => {
  if (typeof window === 'undefined' || sessionStorage.getItem(APP_OPEN_KEY)) return false;
  appOpenRequested = true;
  if (!humanConfirmed) {
    debugAnalytics('app_open deferred', { reason: 'human_not_confirmed' });
    return false;
  }
  const tracked = trackEvent('app_open', {
    entry_path: window.location.pathname,
    referrer_domain: (() => {
      try {
        return document.referrer ? new URL(document.referrer).hostname : 'direct';
      } catch {
        return 'direct';
      }
    })(),
    traffic_source: getFirstTouchSource(),
  });
  if (tracked) sessionStorage.setItem(APP_OPEN_KEY, 'true');
  return tracked;
};

const flushDeferredAppOpen = () => {
  if (appOpenRequested && !sessionStorage.getItem(APP_OPEN_KEY)) {
    trackAppOpen();
  }
};

const markSessionEngaged = (reason) => {
  if (engagedSession) return;
  engagedSession = true;
  analyticsContext = {
    ...analyticsContext,
    engaged_session: true,
  };
  if (isInitialized && analyticsContext.is_authenticated) {
    amplitude.identify(new Identify().set('engaged_session', true));
  }
  debugAnalytics('session engaged', { reason });
};

const confirmHumanSession = (reason) => {
  if (humanConfirmed || isSuspectedBot()) return;
  humanConfirmed = true;
  removeHumanListeners?.();
  removeHumanListeners = null;
  if (humanTimer) {
    clearTimeout(humanTimer);
    humanTimer = null;
  }
  debugAnalytics('human session confirmed', { reason });
  flushDeferredAppOpen();
};

export const startHumanSessionTracking = () => {
  if (typeof window === 'undefined' || humanTrackingStarted) return false;
  humanTrackingStarted = true;

  if (isSuspectedBot()) {
    const detection = getBotDetection();
    debugAnalytics('human session tracking skipped', {
      reason: 'suspected_bot',
      bot_reason: detection.reason,
    });
    return false;
  }

  const onInteraction = (event) => {
    markSessionEngaged(`interaction:${event.type}`);
    confirmHumanSession(`interaction:${event.type}`);
  };
  HUMAN_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, onInteraction, {
      passive: true,
      once: true,
    });
  });
  removeHumanListeners = () => {
    HUMAN_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, onInteraction);
    });
  };

  humanTimer = window.setTimeout(() => {
    if (document.visibilityState === 'visible') {
      confirmHumanSession('visible_timer');
    }
  }, HUMAN_CONFIRMATION_DELAY_MS);

  engagedTimer = window.setTimeout(() => {
    if (document.visibilityState === 'visible') {
      markSessionEngaged('active_time_10_seconds');
    }
  }, ENGAGED_SESSION_DELAY_MS);
  return true;
};

export const trackPageView = (pathname, routeContext = {}) => {
  if (!pathname || pathname.startsWith('/admin')) return false;
  const pageKey = pathname;
  if (lastPageKey === pageKey) return false;
  lastPageKey = pageKey;
  const tracked = trackEvent('page_viewed', {
    page_type: routeContext.page_type || 'other',
    traffic_source: getFirstTouchSource(),
    ...routeContext,
  });
  if (tracked) {
    pageViewCount += 1;
    if (pageViewCount >= 2) markSessionEngaged('two_page_views');
  }
  return tracked;
};

export const __resetAnalyticsForTests = () => {
  isInitialized = false;
  lastPageKey = null;
  pageViewCount = 0;
  humanConfirmed = false;
  humanTrackingStarted = false;
  appOpenRequested = false;
  engagedSession = false;
  if (humanTimer) clearTimeout(humanTimer);
  if (engagedTimer) clearTimeout(engagedTimer);
  removeHumanListeners?.();
  humanTimer = null;
  engagedTimer = null;
  removeHumanListeners = null;
  recentActions.clear();
  analyticsContext = {
    is_authenticated: false,
    role: 'guest',
    is_internal: false,
    engaged_session: false,
  };
};

export default amplitude;
